import { useCallback } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { CeColourField } from "../../generic/components/form-fields/ce-colour-field";
import { CeRadio } from "../../generic/components/form-fields/ce-radio";
import { CeSlider } from "../../generic/components/form-fields/ce-slider";
import { CeToggle } from "../../generic/components/form-fields/ce-toggle";
import { FormStack } from "../../generic/components/form-fields/form-stack";

import type { EmailDesignSchema } from "../../email-design/types/email-design-type";
import type { FC } from "react";

export const LayoutEditor: FC = () => {
  const { control, getValues, setValue } = useFormContext<EmailDesignSchema>();

  useWatch({
    control,
    name: ["layout_settings.column_spacing_type", "layout_settings.column_individual_padding"],
  });
  const [individualPaddingType] = getValues([
    "layout_settings.column_spacing_type",
    "layout_settings.column_individual_padding",
  ]);

  const handleToggleIndividualPaddings = useCallback(
    (checked: boolean): void => setValue("layout_settings.column_individual_padding", checked),
    [setValue],
  );
  return (
    <FormStack>
      <CeColourField<EmailDesignSchema>
        analyticsCategory="email_design"
        name="layout_settings.background_colour"
        label="Background Color"
      />
      <CeColourField<EmailDesignSchema>
        analyticsCategory="email_design"
        name="layout_settings.paper_colour"
        label="Paper Color"
      />
      <CeSlider<EmailDesignSchema>
        analyticsCategory="email_design"
        name="layout_settings.max_paper_width"
        label="Maximum Paper Width"
        min={500}
        max={1000}
        step={50}
      />
      <CeSlider<EmailDesignSchema>
        analyticsCategory="email_design"
        name="layout_settings.paper_padding_side"
        label="Side Padding"
        min={0}
        max={50}
        step={1}
      />
      <CeSlider<EmailDesignSchema>
        analyticsCategory="email_design"
        name="layout_settings.paper_padding_top"
        label="Top Padding"
        min={0}
        max={50}
        step={1}
      />
      <CeSlider<EmailDesignSchema>
        analyticsCategory="email_design"
        name="layout_settings.paper_padding_bottom"
        label="Bottom Padding"
        min={0}
        max={50}
        step={1}
      />
      <CeSlider<EmailDesignSchema>
        analyticsCategory="email_design"
        name="layout_settings.paragraph_padding"
        label="Paragraph Padding"
      />
      <CeColourField<EmailDesignSchema>
        analyticsCategory="email_design"
        name="layout_settings.border.colour"
        label="Border Color"
      />
      <CeSlider<EmailDesignSchema>
        analyticsCategory="email_design"
        name="layout_settings.border.width"
        label="Border Width"
        min={0}
        max={10}
        step={1}
      />

      <CeRadio<EmailDesignSchema>
        analyticsCategory="email_design"
        label="Column Spacing"
        name="layout_settings.column_spacing_type"
        options={[
          { value: "spacing", label: "Outside" },
          { value: "padding", label: "Inside" },
          { value: "none", label: "None" },
        ]}
      />
      <>
        <CeToggle
          labelPlacement="top"
          name="layout_settings.column_individual_padding"
          label="Column Padding Individual Values"
          onChange={handleToggleIndividualPaddings}
        />

        {!individualPaddingType && (
          <CeSlider<EmailDesignSchema>
            analyticsCategory="email_design"
            formControlStyle={{ marginTop: 0 }}
            name="layout_settings.column_padding"
            max={50}
            step={1}
            label="Column Padding"
          />
        )}

        {individualPaddingType && (
          <>
            <CeSlider<EmailDesignSchema>
              analyticsCategory="email_design"
              formControlStyle={{ marginTop: 0 }}
              name="layout_settings.column_padding_top"
              max={50}
              step={1}
              label="Column Padding Top"
            />
            <CeSlider<EmailDesignSchema>
              analyticsCategory="email_design"
              formControlStyle={{ marginTop: 0 }}
              name="layout_settings.column_padding_bottom"
              max={50}
              step={1}
              label="Column Padding Bottom"
            />
            <CeSlider<EmailDesignSchema>
              analyticsCategory="email_design"
              formControlStyle={{ marginTop: 0 }}
              name="layout_settings.column_padding_left"
              max={50}
              step={1}
              label="Column Padding Left"
            />
            <CeSlider<EmailDesignSchema>
              analyticsCategory="email_design"
              formControlStyle={{ marginTop: 0 }}
              name="layout_settings.column_padding_right"
              max={50}
              step={1}
              label="Column Padding Right"
            />
          </>
        )}
      </>
    </FormStack>
  );
};
